/**
 * Wave 80 · 2026-05-24 · Catálogo de ilustrações Builder (Ideogram-burn pack).
 *
 * 134 assets bundlados em /apps/web/public/illustrations:
 *   - 24 empty-states (DESIGN style, isométrico Linear/Notion)
 *   - 5 avatars (supplier/foreman/pm/architect/owner)
 *   - 25 icons (módulos + actions)
 *   - 80 stock arquitetônico BR (REALISTIC, padrão × tipologia)
 *
 * Origem: outputs/ideogram-burn (24/30/100 originais, comprimidos pra
 * 768px DESIGN e 1280px stock). Total bundle web: ~6MB.
 *
 * Uso:
 *   import { illustrations, pickStockByPadrao } from '@/assets/builder-illustrations';
 *   <img src={illustrations.empty['projects'].src} alt={...} />
 *   <EmptyState illustrationKey="projects" title="..." />
 *
 * Não tipar EmptyStateIllustrationKey rígido pra permitir keys novas
 * sem recompilar todas referências; runtime safeguard no componente.
 */

export type EmptyStateIllustrationKey =
  | 'projects'
  | 'team'
  | 'daily-logs'
  | 'inbox'
  | 'photos'
  | 'documents'
  | 'purchases'
  | 'notes'
  | 'schedule'
  | 'checklist'
  | 'inventory'
  | 'shipments'
  | 'budget'
  | 'contracts'
  | 'analytics'
  | 'search'
  | 'notifications'
  | 'sent'
  | 'invites'
  | 'home'
  | 'signatures'
  | 'receipts'
  | 'comments'
  | 'timeline';

export type AvatarRole =
  | 'supplier'
  | 'foreman'
  | 'pm'
  | 'architect'
  | 'owner';

export interface IllustrationEntry {
  src: string;
  alt: string;
}

export interface StockEntry {
  id: string;
  src: string;
  alt: string;
  padrao: 'baixo' | 'medio' | 'alto' | 'luxo';
  tipologia: 'terrea' | 'sobrado' | 'comercial' | 'predio' | 'residencial';
}

export const illustrations = {
  empty: {
    'projects':       { src: '/illustrations/empty-states/es-001.jpg', alt: 'Estaca topográfica em terreno vazio — nenhuma obra criada' },
    'team':           { src: '/illustrations/empty-states/es-002.jpg', alt: 'Capacete amarelo isolado — nenhum membro de equipe' },
    'daily-logs':     { src: '/illustrations/empty-states/es-003.jpg', alt: 'Prancheta vazia — nenhum RDO registrado' },
    'inbox':          { src: '/illustrations/empty-states/es-004.jpg', alt: 'Bandeja de papel vazia — caixa de entrada zerada' },
    'photos':         { src: '/illustrations/empty-states/es-005.jpg', alt: 'Porta-retrato vazio — nenhuma foto enviada' },
    'documents':      { src: '/illustrations/empty-states/es-006.jpg', alt: 'Pasta carimbada vazia — nenhum documento anexado' },
    'purchases':      { src: '/illustrations/empty-states/es-007.jpg', alt: 'Carrinho vazio — nenhuma ordem de compra' },
    'notes':          { src: '/illustrations/empty-states/es-008.jpg', alt: 'Caderno aberto com lápis — nenhuma anotação' },
    'schedule':       { src: '/illustrations/empty-states/es-009.jpg', alt: 'Calendário com semana destacada — agenda vazia' },
    'checklist':      { src: '/illustrations/empty-states/es-010.jpg', alt: 'Prancheta de checklist em branco — nenhuma tarefa' },
    'inventory':      { src: '/illustrations/empty-states/es-011.jpg', alt: 'Caixa de ferramentas vazia — sem estoque' },
    'shipments':      { src: '/illustrations/empty-states/es-012.jpg', alt: 'Caixa de mudança entreaberta — nenhuma entrega' },
    'budget':         { src: '/illustrations/empty-states/es-013.jpg', alt: 'Carteira aberta vazia — sem verba alocada' },
    'contracts':      { src: '/illustrations/empty-states/es-014.jpg', alt: 'Aperto de mão vazio — nenhum contrato' },
    'analytics':      { src: '/illustrations/empty-states/es-015.jpg', alt: 'Gráfico no mínimo — sem dados pra analisar' },
    'search':         { src: '/illustrations/empty-states/es-016.jpg', alt: 'Lupa sobre grid vazio — nenhum resultado encontrado' },
    'notifications':  { src: '/illustrations/empty-states/es-017.jpg', alt: 'Caixa de correio vazia — sem notificações' },
    'sent':           { src: '/illustrations/empty-states/es-018.jpg', alt: 'Avião de papel partindo — nenhum item enviado' },
    'invites':        { src: '/illustrations/empty-states/es-019.jpg', alt: 'Envelope aberto — nenhum convite pendente' },
    'home':           { src: '/illustrations/empty-states/es-020.jpg', alt: 'Silhueta de casa neutra — sem destinos cadastrados' },
    'signatures':     { src: '/illustrations/empty-states/es-021.jpg', alt: 'Assinatura ondulada — nenhuma assinatura coletada' },
    'receipts':       { src: '/illustrations/empty-states/es-022.jpg', alt: 'Recibo curvado — nenhum recibo registrado' },
    'comments':       { src: '/illustrations/empty-states/es-023.jpg', alt: 'Balão de pensamento — nenhum comentário' },
    'timeline':       { src: '/illustrations/empty-states/es-024.jpg', alt: 'Linha do tempo com marcador único — cronograma vazio' },
  } as Record<EmptyStateIllustrationKey, IllustrationEntry>,

  avatars: {
    'supplier':   { src: '/illustrations/assets/as-001.jpg', alt: 'Avatar de fornecedor' },
    'foreman':    { src: '/illustrations/assets/as-002.jpg', alt: 'Avatar de mestre de obras' },
    'pm':         { src: '/illustrations/assets/as-003.jpg', alt: 'Avatar de gerente de projeto' },
    'architect':  { src: '/illustrations/assets/as-004.jpg', alt: 'Avatar de arquiteto' },
    'owner':      { src: '/illustrations/assets/as-005.jpg', alt: 'Avatar de proprietário' },
  } as Record<AvatarRole, IllustrationEntry>,

  icons: {
    'trena':        { src: '/illustrations/assets/as-006.jpg', alt: 'Trena' },
    'compras':      { src: '/illustrations/assets/as-007.jpg', alt: 'Compras' },
    'trust':        { src: '/illustrations/assets/as-008.jpg', alt: 'Trust Score' },
    'autopilot':    { src: '/illustrations/assets/as-009.jpg', alt: 'Auto-piloto' },
    'checklist':    { src: '/illustrations/assets/as-010.jpg', alt: 'Checklist FVS' },
    'risk':         { src: '/illustrations/assets/as-011.jpg', alt: 'Risco / Alerta' },
    'etapas':       { src: '/illustrations/assets/as-012.jpg', alt: 'Etapas / Cronograma' },
    'photos':       { src: '/illustrations/assets/as-013.jpg', alt: 'Fotos' },
    'whatsapp':     { src: '/illustrations/assets/as-014.jpg', alt: 'WhatsApp' },
    'pranchas':     { src: '/illustrations/assets/as-015.jpg', alt: 'Pranchas / Desenhos' },
    'docs':         { src: '/illustrations/assets/as-016.jpg', alt: 'Documentos' },
    'verba':        { src: '/illustrations/assets/as-017.jpg', alt: 'Verba' },
    'estoque':      { src: '/illustrations/assets/as-018.jpg', alt: 'Estoque' },
    'curva-abc':    { src: '/illustrations/assets/as-019.jpg', alt: 'Curva ABC' },
    'brain':        { src: '/illustrations/assets/as-020.jpg', alt: 'Brain AI' },
    'mapa':         { src: '/illustrations/assets/as-021.jpg', alt: 'Mapa / Local' },
    'weather':      { src: '/illustrations/assets/as-022.jpg', alt: 'Clima' },
    'voice':        { src: '/illustrations/assets/as-023.jpg', alt: 'Voz / Microfone' },
    'search-site':  { src: '/illustrations/assets/as-024.jpg', alt: 'Busca em obra' },
    'handoff':      { src: '/illustrations/assets/as-025.jpg', alt: 'Entrega de chaves' },
    'goals':        { src: '/illustrations/assets/as-026.jpg', alt: 'Metas' },
    'sync':         { src: '/illustrations/assets/as-027.jpg', alt: 'Sincronização' },
    'bell':         { src: '/illustrations/assets/as-028.jpg', alt: 'Notificação' },
    'approve':      { src: '/illustrations/assets/as-029.jpg', alt: 'Aprovar' },
    'savings':      { src: '/illustrations/assets/as-030.jpg', alt: 'Economia' },
  } as Record<string, IllustrationEntry>,
} as const;

/**
 * Stock arquitetônico BR — 80 fotos REALISTAS por padrão × tipologia.
 * Embaralhadas determinísticamente por projectId pra mesmo projeto
 * sempre pegar a mesma foto de fallback.
 */
export const stockArch: StockEntry[] = [
  // Auto-gerado por Wave 80 — fonte outputs/ideogram-burn/stock-arch/sa-*.json
  // (lista carregada via fetch ou estática; aqui usamos estática pra TS strict)
  ...buildStockList(),
];

function buildStockList(): StockEntry[] {
  // W158 · 2026-06-02 — só os 30 arquivos que existem em disco
  // (public/illustrations/stock-arch/sa-001..sa-030.jpg). O catálogo antigo
  // listava sa-001..sa-088, mas 31-88 nunca foram entregues: o default
  // medio/terrea caía em sa-051..055 (inexistentes) → 404 → a Vitrine
  // renderizava o alt text "Fachada padrão medio (terrea)" no lugar da foto.
  // pickStockByPadrao tem fallback (padrão → pool inteiro), então combos que
  // só existiam em 31-88 resolvem para um arquivo válido em vez de quebrar.
  const data: Array<[number, 'baixo'|'medio'|'alto'|'luxo', 'terrea'|'sobrado'|'comercial'|'predio'|'residencial']> = [
    [1,'alto','terrea'],[2,'alto','terrea'],[3,'alto','terrea'],[4,'alto','terrea'],[5,'luxo','sobrado'],
    [6,'luxo','sobrado'],[7,'luxo','sobrado'],[8,'luxo','sobrado'],[9,'luxo','sobrado'],[10,'luxo','comercial'],
    [11,'luxo','comercial'],[12,'luxo','comercial'],[13,'luxo','comercial'],[14,'luxo','comercial'],[15,'luxo','comercial'],
    [16,'luxo','comercial'],[17,'luxo','comercial'],[18,'medio','comercial'],[19,'medio','comercial'],[20,'medio','comercial'],
    [21,'medio','comercial'],[22,'baixo','comercial'],[23,'baixo','comercial'],[24,'baixo','comercial'],[25,'baixo','comercial'],
    [26,'alto','comercial'],[27,'alto','comercial'],[28,'alto','comercial'],[29,'alto','comercial'],[30,'medio','predio'],
  ];
  return data.map(([n, padrao, tipologia]) => ({
    id: String(n).padStart(3, '0'),
    padrao,
    tipologia,
    src: `/illustrations/stock-arch/sa-${String(n).padStart(3, '0')}.jpg`,
    alt: `Fachada padrão ${padrao} (${tipologia})`,
  }));
}

/**
 * Escolhe stock determinístico por padrão × tipologia + seed (projectId).
 * Mesma seed sempre retorna mesma foto.
 */
export function pickStockByPadrao(
  padrao: StockEntry['padrao'] | string | null | undefined,
  tipologia: StockEntry['tipologia'] | string | null | undefined,
  seed: string | number = 0,
): StockEntry {
  const wanted = (padrao || 'medio').toLowerCase() as StockEntry['padrao'];
  const wantedTipo = (tipologia || 'terrea').toLowerCase() as StockEntry['tipologia'];
  let pool = stockArch.filter(s => s.padrao === wanted && s.tipologia === wantedTipo);
  if (pool.length === 0) pool = stockArch.filter(s => s.padrao === wanted);
  if (pool.length === 0) pool = stockArch;
  const hash = typeof seed === 'number' ? seed : fnv1a(String(seed));
  return pool[hash % pool.length]!;
}

/**
 * Avatar fallback por role. Usado quando profile photo ausente.
 */
export function pickAvatarByRole(role: string | null | undefined): IllustrationEntry {
  const r = (role || '').toLowerCase();
  if (r.includes('supplier') || r.includes('fornecedor')) return illustrations.avatars.supplier;
  if (r.includes('foreman') || r.includes('mestre')) return illustrations.avatars.foreman;
  if (r.includes('architect') || r.includes('arquiteto')) return illustrations.avatars.architect;
  if (r.includes('owner') || r.includes('client') || r.includes('proprietario') || r.includes('proprietário')) {
    return illustrations.avatars.owner;
  }
  return illustrations.avatars.pm;
}

function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h;
}
